import {
  FC, useEffect, useState, useCallback,
} from 'react';

import { Button, EmptyState } from 'shared/ui';
import { UserDetailResponseDto, UserRequestDto } from 'shared/api';
import { UserListItem } from 'entities/users';
import { showConfirm } from 'shared/utils';
import { useUsers } from '../hooks/useUsers';
import { UserTable, UserForm } from '../components';

import styles from './UsersPage.module.scss';

export const UsersPage: FC = () => {
  const {
    users,
    loadUsers,
    createNewUser,
    updateExistingUser,
    userDetail,
    loadUserDetail,
  } = useUsers();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRequestDto | null>(null);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const handleCreate = useCallback(() => {
    setEditingUser(null);
    setEditingUserId(null);
    setIsModalOpen(true);
  }, []);

  const mapDetailToForm = useCallback((detail?: UserDetailResponseDto | null): UserRequestDto | null => {
    if (!detail) return null;
    return {
      lastname: detail.lastname || '',
      firstname: detail.firstname || '',
      middleName: detail.middleName,
      company: detail.company,
      innCompany: detail.innCompany,
      email: detail.email || '',
      status: detail.status as UserRequestDto.status,
      comment: detail.comment,
      keycloakUserId: detail.keycloakUserId,
    };
  }, []);

  // Отслеживаем изменения userDetail из Redux
  useEffect(() => {
    if (userDetail && editingUserId) {
      setEditingUser(mapDetailToForm(userDetail));
    }
  }, [userDetail, editingUserId, mapDetailToForm]);

  const handleEdit = useCallback(async (user: UserListItem) => {
    setEditingUserId(user.id || null);
    setIsModalOpen(true);
    setEditingUser(null);
    if (user.id) {
      await loadUserDetail(user.id);
    }
  }, [loadUserDetail]);

  const handleDelete = useCallback(async (id: number, data: UserRequestDto) => {
    // eslint-disable-next-line no-alert
    showConfirm({
      title: 'Вы уверены, что хотите удалить этот отзыв?',
      onOk: async () => {
        await updateExistingUser(id, { ...data, status: UserRequestDto.status.SUSPENDED });
      },
    });
  }, [updateExistingUser]);

  const handleSubmit = useCallback(async (data: UserRequestDto) => {
    // При редактировании добавляем keycloakUserId из исходных данных
    const userData = editingUserId && editingUser
      ? { ...data, keycloakUserId: editingUser.keycloakUserId }
      : data;

    const isEditing = !!editingUserId;
    const result = isEditing
      ? await updateExistingUser(editingUserId, userData)
      : await createNewUser(userData);

    if (result.success) {
      setIsModalOpen(false);
      setEditingUser(null);
      setEditingUserId(null);
    }
  }, [editingUserId, editingUser, updateExistingUser, createNewUser]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditingUser(null);
    setEditingUserId(null);
  }, []);

  return (
    <div className={styles.usersPage}>
      <div className={styles.header}>
        <h1>Пользователи</h1>
        <Button onClick={handleCreate}>Создать пользователя</Button>
      </div>

      {users.length === 0 ? (
        <EmptyState
          message="Пользователи не найдены"
          icon="👥"
          description="Начните с создания нового пользователя или проверьте настройки фильтров."
          onCreateNew={handleCreate}
          createButtonText="Создать первого пользователя"
        />
      ) : (
        <UserTable
          users={users}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <UserForm
        isOpen={isModalOpen}
        editingUser={editingUser}
        isEditing={!!editingUserId}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </div>
  );
};
